import { normalizePathname } from './normalizePathname';

// Meta (Facebook/Instagram) Pixel — browser pixel, marketing site only.
//
// Loaded lazily and guarded: the pixel only initializes when
// REACT_APP_META_PIXEL_ID is set (CRA convention, mirrors REACT_APP_FIREBASE_*).
// With the var absent/empty every export below is a clean no-op — no script
// injected, no `fbq` defined, zero console errors. The ID is never hardcoded;
// `.env` is gitignored, so dev builds and the live site without a configured ID
// don't break.
//
// Scope: marketing routes only. The SPA re-fire + first init are driven by
// `useMetaPixel` (src/hooks/useMetaPixel.js), which gates on `isMarketingPath`
// so the pixel never loads on /portal, /login, or the noindex app surfaces.
//
// v1 is browser pixel only. Server-side Purchase via the Conversions API (off
// the Stripe webhook in Functions) is a separate fast-follow — see the handoff.

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// sessionStorage key for the pending-purchase hand-off. The buy form stashes the
// amount here right before the Stripe redirect; the success surface reads it on
// return to fire Purchase with the true value. sessionStorage is per-tab and
// survives the cross-origin trip to Stripe and back.
const PENDING_PURCHASE_KEY = 'cyv:pendingPurchase';

// Our own init guard. We can't unload `fbevents.js` once injected, so this just
// records whether we've run the base snippet. PageView is fired by the hook
// (not by init) so the first load and every SPA nav each count exactly once.
let initialized = false;

// Mirrors App.js `isNoindexRoute` (inverted): the marketing site is every route
// that isn't a /portal app surface, a legacy auth page, or a noindex utility
// page. Keep this in lockstep with that predicate — those are the pages that
// are not ad destinations and must not be tracked.
export function isMarketingPath(pathname) {
	if (typeof pathname !== 'string') return false;
	/* Same trailing-slash normalisation App.js applies, for the same reason —
	   without it `/login/` reads as a marketing path and fires a PageView on a
	   page that must not be tracked. The lockstep note above is why this is
	   fixed here at the same time rather than only in App.js. */
	const path = normalizePathname(pathname);
	if (path.startsWith('/portal')) return false;
	if (path === '/employee/login' || path === '/customer/login') return false;
	if (['/login', '/sign-up', '/requests'].includes(path)) {
		return false;
	}
	return true;
}

// True once the base snippet has been injected and `fbq` is available.
export function isMetaPixelReady() {
	return initialized && typeof window !== 'undefined' && typeof window.fbq === 'function';
}

// Inject the standard Meta base pixel and call fbq('init'). Does NOT fire
// PageView — the hook owns PageView so the first load and every SPA nav each
// fire exactly once regardless of React effect order. Idempotent and a clean
// no-op without a configured ID. The `fbq` stub queues calls synchronously
// until fbevents.js loads, so callers can track immediately after.
export function initMetaPixel() {
	if (initialized) return;
	if (!PIXEL_ID) return; // no ID configured → no-op, nothing injected
	if (typeof window === 'undefined' || typeof document === 'undefined') return;

	/* eslint-disable */
	!(function (f, b, e, v, n, t, s) {
		if (f.fbq) return;
		n = f.fbq = function () {
			n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
		};
		if (!f._fbq) f._fbq = n;
		n.push = n;
		n.loaded = !0;
		n.version = '2.0';
		n.queue = [];
		t = b.createElement(e);
		t.async = !0;
		t.src = v;
		s = b.getElementsByTagName(e)[0];
		s.parentNode.insertBefore(t, s);
	})(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
	/* eslint-enable */

	initialized = true;
	window.fbq('init', PIXEL_ID);
}

// Fire PageView. Called by the hook on the first marketing load and on every
// subsequent SPA route change. No-op until the pixel is initialized.
export function trackPageView() {
	if (!isMetaPixelReady()) return;
	window.fbq('track', 'PageView');
}

// Lead — fired from TWO success branches, after the relevant callable resolves
// OK: /contact (inquiry) and /trial (booking, `TrialLesson.js`). It said
// "only from the /contact success branch" until 2026-08-11; the trial caller
// landed 2026-08-08 and the comment was never updated.
//
// CONSEQUENCE FOR AD SETUP, since it is not obvious from here: a Meta campaign
// optimising on `Lead` is optimising on a MIXED signal — inquiries and trial
// bookings together. That is why the Aug 2026 free-trial campaign runs a
// Traffic objective rather than Leads. If a future campaign wants to optimise
// on trial bookings specifically, this needs to become a distinct event first.
export function trackLead() {
	if (!isMetaPixelReady()) return;
	window.fbq('track', 'Lead');
}

// Purchase — fired on the Stripe checkout success surface. `value` is the true
// amount paid in dollars (packs are flat-priced with no client-side coupons, so
// it equals Stripe's amount_total); currency defaults to USD. If value is
// missing/invalid we fire Purchase WITHOUT it (documented fallback) rather than
// sending a bogus number — flagged for the Conversions API follow-up.
export function trackPurchase({ value, currency = 'USD' } = {}) {
	if (!isMetaPixelReady()) return;
	const num = Number(value);
	if (Number.isFinite(num) && num > 0) {
		window.fbq('track', 'Purchase', { value: num, currency });
	} else {
		window.fbq('track', 'Purchase');
	}
}

// Called right before the Stripe redirect to remember the amount being paid.
// The success_url carries no session id or amount, but the client knows the
// flat pack price here — and with no client-side coupons that equals Stripe's
// amount_total. No-op for non-positive/invalid amounts.
export function stashPendingPurchase({ value, currency = 'USD' } = {}) {
	if (typeof window === 'undefined' || !window.sessionStorage) return;
	const num = Number(value);
	if (!Number.isFinite(num) || num <= 0) return;
	try {
		window.sessionStorage.setItem(
			PENDING_PURCHASE_KEY,
			JSON.stringify({ value: num, currency })
		);
	} catch (_) {
		/* storage unavailable (private mode / quota) — skip silently */
	}
}

// Clear the pending-purchase stash WITHOUT firing anything. Called on the
// cancelled-checkout surfaces so a later history-navigation to a stale
// ?status=success URL has nothing armed to fire (Review 3 #37). Safe to call
// unconditionally — no-op if nothing is stashed or storage is unavailable.
export function clearPendingPurchase() {
	if (typeof window === 'undefined' || !window.sessionStorage) return;
	try {
		window.sessionStorage.removeItem(PENDING_PURCHASE_KEY);
	} catch (_) {
		/* storage unavailable (private mode / quota) — nothing to clear */
	}
}

// Fire Purchase from a stashed pending-purchase, then clear it. Clearing first
// guarantees a reload or bookmarked ?status=success can't double-count. Ensures
// the pixel is loaded first so this doesn't race the route-change hook's init.
// Safe to call unconditionally on the success surface — no-op if nothing is
// stashed or the pixel is unconfigured.
export function firePendingPurchase() {
	if (typeof window === 'undefined' || !window.sessionStorage) return;
	let raw = null;
	try {
		raw = window.sessionStorage.getItem(PENDING_PURCHASE_KEY);
		if (raw) window.sessionStorage.removeItem(PENDING_PURCHASE_KEY);
	} catch (_) {
		return;
	}
	if (!raw) return;
	initMetaPixel(); // idempotent; load the lib if the hook hasn't yet
	try {
		const { value, currency } = JSON.parse(raw);
		trackPurchase({ value, currency });
	} catch (_) {
		trackPurchase({}); // corrupt payload → fire without value (fallback)
	}
}
